import type { ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * Widgets de l'Accueil (vague 7, préparation) : l'Accueil personnalisable par membre (choix, ordre,
 * taille) sera assemblé par L5 après le contrat de la vague 7. Ici, seulement les briques :
 * - `WidgetGrid` : grille de 4 colonnes sur ordinateur, 2 sur tablette, 1 sur téléphone ;
 * - `Widget` : surface fine (filet, sans ombre), taille `s` (1 colonne), `m` (2), `l` (toute la largeur) ;
 * - `WidgetHeader` : icône teintée selon le ton, titre, compteur et action (lien « Tout voir »).
 * Sur téléphone, un widget `s` occupe une demi-largeur (deux chiffres côte à côte), les autres la largeur.
 */
export type WidgetSize = 's' | 'm' | 'l';
export type WidgetTone = 'neutral' | 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'author-customer' | 'author-support';

const SPAN: Record<WidgetSize, string> = {
  s: 'col-span-1',
  m: 'col-span-2',
  l: 'col-span-2 xl:col-span-4',
};

const ICON_TONE: Record<WidgetTone, string> = {
  neutral: 'bg-sunken text-ink-2',
  brand: 'bg-brand-soft text-brand-strong',
  info: 'bg-info-bg text-info-fg',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
  'author-customer': 'bg-author-customer-bg text-author-customer-fg',
  'author-support': 'bg-author-support-bg text-author-support-fg',
};

export function WidgetGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('grid grid-flow-row-dense grid-cols-2 gap-2.5 xl:grid-cols-4 xl:gap-3', className)}>{children}</div>;
}

export function Widget({
  size = 'm',
  children,
  className,
  as: Tag = 'section',
  ...rest
}: {
  size?: WidgetSize;
  children: ReactNode;
  className?: string;
  as?: 'section' | 'div' | 'li';
  'aria-labelledby'?: string;
  'data-widget'?: string;
}) {
  return (
    <Tag className={cx('flex min-w-0 flex-col rounded-2xl border border-line bg-surface p-(--card-p)', SPAN[size], className)} {...rest}>
      {children}
    </Tag>
  );
}

export function WidgetHeader({
  id,
  title,
  icon,
  tone = 'neutral',
  count,
  action,
  className,
}: {
  /** Identifiant du titre, pour `aria-labelledby` du widget. */
  id?: string;
  title: ReactNode;
  icon?: ReactNode;
  tone?: WidgetTone;
  /** Nombre affiché à côté du titre (élément de liste, tâches). */
  count?: number;
  /** Lien ou bouton discret à droite (« Tout voir »). */
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('mb-2 flex min-h-7 items-center gap-2', className)}>
      {icon ? <span className={cx('grid size-7 shrink-0 place-items-center rounded-md', ICON_TONE[tone])}>{icon}</span> : null}
      <h2 id={id} className="min-w-0 flex-1 truncate text-base font-medium text-ink">
        {title}
        {count !== undefined ? <span className="ml-1.5 font-normal text-ink-3 tabular-nums">{count}</span> : null}
      </h2>
      {action}
    </div>
  );
}

/** Chiffre clé d'un widget `s` : valeur, libellé, détail facultatif. */
export function WidgetStat({ value, label, detail }: { value: ReactNode; label: ReactNode; detail?: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-3xl leading-none font-semibold tracking-[-0.02em] text-ink tabular-nums">{value}</p>
      <p className="mt-1 text-sm text-ink-2">{label}</p>
      {detail ? <p className="mt-0.5 text-xs text-ink-3">{detail}</p> : null}
    </div>
  );
}
